import { useState } from 'react';
import { post } from '../lib/api';

// Server error code -> friendly copy.
const ERRORS: Record<string, string> = {
  missing_code: 'Enter your signup code.',
  invalid_code: "That code isn't valid. Double-check it with the person who invited you.",
  code_revoked: 'That code has been revoked. Ask for a new one.',
  code_used: 'That code has already been used.',
  code_expired: 'That code has expired. Ask for a new one.',
  invalid_username: 'Pick a username 2–24 characters long (letters, numbers, _ or -).',
  username_taken: 'That username is taken — try another.',
  missing_password: 'Enter a password.',
  password_too_short: 'Use at least 8 characters for your password.',
  password_mismatch: 'The passwords do not match.',
};

export function Join() {
  const [code, setCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [doneUsername, setDoneUsername] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    if (password !== confirmPassword) {
      setErr(ERRORS.password_mismatch);
      setBusy(false);
      return;
    }
    try {
      const res = await post<{ ok: boolean; username: string }>('/join/redeem', {
        Code: code.trim(),
        Username: username.trim(),
        Password: password,
      });
      setDoneUsername(res.username);
    } catch (e) {
      const key = (e as Error).message;
      setErr(ERRORS[key] ?? "We couldn't create the account. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (doneUsername) {
    return (
      <div className="mx-auto max-w-xl">
        <div className="card overflow-hidden text-center">
          <div className="h-1.5 bg-success" />
          <div className="p-7 sm:p-9">
            <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-success/15 text-2xl text-success" aria-hidden="true">
              ✓
            </div>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-success">Account ready</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-ink-50">Welcome, {doneUsername}</h1>
            <p className="mx-auto mt-3 max-w-md text-sm leading-6 text-ink-300">
              Open the RR Tsunami launcher and sign in with the username and password you just created.
            </p>
            <div className="mt-6 rounded-lg border border-ink-700 bg-ink-950/70 px-4 py-3 text-left text-xs leading-5 text-ink-400">
              Keep your password private. RR Tsunami will never ask you to send it in a message.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto grid max-w-4xl gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
      <section className="space-y-5 py-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-300">RR Tsunami account</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-ink-50 sm:text-4xl">Create your username</h1>
          <p className="mt-3 max-w-md text-sm leading-6 text-ink-300">
            Choose the name and password you will use to sign in from the game. You only need to do this once.
          </p>
        </div>

        <ol className="space-y-3 text-sm text-ink-300">
          <li className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-xs font-semibold text-brand-200">1</span>
            Enter the one-time code from your invitation.
          </li>
          <li className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-xs font-semibold text-brand-200">2</span>
            Pick a unique username and a private password.
          </li>
          <li className="flex gap-3">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-xs font-semibold text-brand-200">3</span>
            Sign in through the RR Tsunami launcher.
          </li>
        </ol>

        <p className="rounded-lg border border-ink-800 bg-ink-900/60 px-4 py-3 text-xs leading-5 text-ink-400">
          RR Tsunami is an independent private testing service and is not operated by Rec Room Inc.
        </p>
      </section>

      <form onSubmit={submit} className="card !p-5 space-y-4 sm:!p-7">
        <div>
          <h2 className="text-lg font-semibold text-ink-50">Your account details</h2>
          <p className="mt-1 text-xs text-ink-400">All fields are required.</p>
        </div>

        <label className="block">
          <span className="label">Signup code</span>
          <input
            className="input mt-1 font-mono tracking-wider"
            value={code}
            onChange={(e) => {
              const compact = e.target.value
                .toUpperCase()
                .replace(/[^A-Z2-9]/g, '')
                .slice(0, 8);
              setCode(compact.length > 4
                ? `${compact.slice(0, 4)}-${compact.slice(4)}`
                : compact);
            }}
            placeholder="XXXX-XXXX"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={9}
            required
            autoFocus
          />
          <span className="mt-1 block text-xs text-ink-400">Codes can be used once.</span>
        </label>

        <label className="block">
          <span className="label">Choose a username</span>
          <input
            className="input mt-1"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="2–24 characters"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            minLength={2}
            maxLength={24}
            pattern="[A-Za-z0-9_-]{2,24}"
            title="Use 2–24 letters, numbers, underscores, or hyphens."
            required
          />
          <span className="mt-1 block text-xs text-ink-400">Letters, numbers, underscores, and hyphens only.</span>
        </label>

        <div className="space-y-2">
          <label className="block">
            <span className="label">Password</span>
            <input
              className="input mt-1"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>

          <label className="block">
            <span className="label">Confirm password</span>
            <input
              className="input mt-1"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Type it again"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>

          <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-ink-300">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="size-4 rounded border-ink-600 bg-ink-900 accent-brand-500"
            />
            Show passwords
          </label>
        </div>

        {err && (
          <div role="alert" aria-live="polite" className="rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">{err}</div>
        )}

        <button type="submit" disabled={busy} className="btn-primary text-sm w-full justify-center">
          {busy ? 'Creating your account…' : 'Create account'}
        </button>

        <p className="text-center text-[11px] leading-5 text-ink-500">
          Your password is sent only to RR Tsunami over the site connection and stored as a one-way hash.
          {' '}Read the <a href="/privacy/" className="text-brand-300 hover:text-brand-200">privacy policy</a>.
        </p>
      </form>
    </div>
  );
}
